import React from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaEnvelope,
  FaMapMarkerAlt,
  FaShareAltSquare,
  FaTimes,
  FaTrash,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";

import {
  DEFAULT_EVENT_IMAGE,
  canDeleteEvent,
  canEditEvent,
  canInviteToEvent,
  canViewCompetitors,
  formatCompetitorLabel,
  formatEventDate,
  formatEventDay,
  formatEventMonth,
  formatEventYear,
  getStatusLabel,
  getStatusStyle,
  hasEventResults,
} from "../utils/eventsJudokaUtils";

function EventMeta({ icon: Icon, label, value }) {
  if (!value) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 shrink-0 text-sky-700 dark:text-sky-200" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function EventCard({
  event,
  adherentId,
  user,
  competitors = [],
  competitorsLoaded,
  onFetchCompetitors,
  onEdit,
  onDelete,
  onViewResults,
  onSendInvitation,
  onStatusChange,
}) {
  const status = event?.AdherentEvent?.status;
  const isEditable = canEditEvent(event, adherentId);
  const isDeletable = canDeleteEvent(event, adherentId);
  const eventHasResults = hasEventResults(event);

  const categories = String(event?.cate_event || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return (
    <article className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="relative min-h-56 overflow-hidden bg-slate-950">
        <img
          src={event?.photo_url || DEFAULT_EVENT_IMAGE}
          alt={event?.titre || "Événement"}
          className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 group-hover:scale-105"
          onError={(eventImage) => {
            eventImage.currentTarget.src = DEFAULT_EVENT_IMAGE;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

        <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-xl">
          <div className="bg-gradient-to-br from-blue-600 to-orange-500 px-4 py-2 text-white">
            <p className="text-3xl font-bold leading-none">
              {formatEventDay(event?.start)}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em]">
              {formatEventMonth(event?.start)}
            </p>
          </div>
          <p className="px-3 py-1 text-xs font-bold text-slate-500">
            {formatEventYear(event?.start)}
          </p>
        </div>

        <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2">
          {event?.is_shared && (
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white backdrop-blur-xl">
              <FaShareAltSquare />
              Partagé
            </span>
          )}

          {!event?.is_shared && event?.parent_event_id && (
            <span className="inline-flex items-center gap-2 rounded-full bg-sky-500/90 px-3 py-2 text-xs font-bold text-white backdrop-blur-xl">
              Copie personnelle
            </span>
          )}

          {eventHasResults && (
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-3 py-2 text-xs font-bold text-slate-950 shadow-lg">
              <FaTrophy />
              Résultat
            </span>
          )}

          {status && (
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${getStatusStyle(
                status,
              )}`}
            >
              {getStatusLabel(status)}
            </span>
          )}
        </div>

        <div className="absolute bottom-4 left-4 right-4 text-white">
          {/* <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold backdrop-blur-xl">
            <FaCalendarAlt />
            {formatEventDate(event?.start)}
          </p> */}

          <h3 className="mt-3 line-clamp-2 font-titre text-2xl font-bold leading-tight">
            {event?.titre || "Événement sans titre"}
          </h3>
        </div>
      </div>

      {status === "pending" && (
        <div className="border-b border-orange-200 bg-orange-50 p-4 dark:border-orange-400/20 dark:bg-orange-500/10">
          <p className="text-sm font-bold text-orange-800 dark:text-orange-100">
            Invitation en attente de validation
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => onStatusChange(event.AdherentEvent, "accepted")}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5"
            >
              <FaCheck />
              Je m'inscris
            </button>

            <button
              type="button"
              onClick={() => onStatusChange(event.AdherentEvent, "rejected")}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5"
            >
              <FaTimes />
              Je ne m'inscris pas
            </button>
          </div>
        </div>
      )}

      <div className="p-5">
        <p className="line-clamp-3 text-sm font-medium leading-7 text-slate-600 dark:text-white/55">
          {event?.description || "Aucune description disponible."}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {event?.type_event && (
            <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
              {event.type_event}
            </span>
          )}

          {event?.level_event && (
            <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
              {event.level_event}
            </span>
          )}

          {categories.map((category) => (
            <span
              key={category}
              className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60"
            >
              {category}
            </span>
          ))}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <EventMeta
            icon={FaMapMarkerAlt}
            label="Lieu"
            value={event?.lieu_event || "Non spécifié"}
          />

          <EventMeta
            icon={FaCalendarAlt}
            label="Horaires"
            value={event?.horaire_event || "Non spécifié"}
          />
        </div>

        {canViewCompetitors(user) && (
          <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                  <FaUsers />
                </div>

                <div>
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    Inscrits
                  </p>
                  <p className="text-xs font-medium text-slate-500 dark:text-white/50">
                    Liste des compétiteurs rattachés à cet événement
                  </p>
                </div>
              </div>

              {!competitorsLoaded && (
                <button
                  type="button"
                  onClick={() => onFetchCompetitors(event.id)}
                  className="inline-flex min-h-10 items-center justify-center rounded-2xl bg-slate-950 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
                >
                  Voir les inscrits
                </button>
              )}
            </div>

            {competitorsLoaded && (
              <div className="mt-4">
                {competitors.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {competitors.map((adherent) => (
                      <span
                        key={adherent.id}
                        className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700 shadow-sm dark:bg-white/10 dark:text-white"
                      >
                        {formatCompetitorLabel(adherent)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-slate-500 dark:text-white/50">
                    Aucun inscrit pour le moment.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-5 grid gap-2 border-t border-slate-200 pt-5 dark:border-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {onViewResults && (
            <button
              type="button"
              onClick={() => onViewResults(event)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-amber-400 px-4 py-2 text-xs font-bold text-slate-950 transition hover:-translate-y-0.5"
            >
              <FaTrophy />
              Résultats
            </button>
          )}

          <button
            type="button"
            onClick={() => onEdit(event)}
            disabled={!isEditable}
            title={
              isEditable
                ? "Modifier cet événement"
                : "Seuls les événements créés par ce judoka peuvent être modifiés"
            }
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
          >
            <FaEdit />
            Modifier
          </button>

          <button
            type="button"
            onClick={() => onDelete(event)}
            disabled={!isDeletable}
            title={
              isDeletable
                ? "Supprimer cet événement de l'agenda du judoka"
                : "Un événement partagé ne peut pas être supprimé depuis l'espace judoka"
            }
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
          >
            <FaTrash />
            Supprimer
          </button>

          {canInviteToEvent(user) && (
            <button
              type="button"
              onClick={() => onSendInvitation(event)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5"
            >
              <FaEnvelope />
              Inviter
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default EventCard;
